import { useCardPopoverInputFocus } from '@hooks/card';
import { type Action, type CardNameFields, UpdateNameFieldAction } from '@hooks/use-card-names-state';
import { Box, Stack, TextField } from '@ui-lib';
import { useState } from 'react';

import styles from './CardName.module.scss';
import { NameFieldSuggestions } from './NameFieldSuggestion';
import type { FieldVisibility } from './types';

interface NameFieldsProps {
  state: CardNameFields;
  updateState: (action: Action) => void;
}

export const NameFields = ({ state, updateState }: NameFieldsProps): JSX.Element => {
  const [fieldVisibility, setFieldVisibility] = useState<FieldVisibility>({
    honorificPrefix: Boolean(state.honorificPrefix),
    preferredName: Boolean(state.preferredName),
    middleName: Boolean(state.middleName),
    honorificSuffix: Boolean(state.honorificSuffix),
    maidenName: Boolean(state.maidenName),
    pronouns: Boolean(state.pronouns),
  });

  const onFocus = (inputNode: HTMLInputElement) => {
    inputNode.setSelectionRange(inputNode.value.length, inputNode.value.length);
  };

  const { inputRef } = useCardPopoverInputFocus({ onFocus });

  return (
    <div className={styles['user-name']}>
      <Stack gap="medium">
        {(fieldVisibility.honorificPrefix || state.honorificPrefix) && (
          <TextField
            value={state.honorificPrefix}
            label="Prefix"
            onChange={(event) =>
              updateState({
                type: UpdateNameFieldAction.UPDATE_PREFIX,
                value: event.currentTarget.value,
              })
            }
            onClear={() => updateState({ type: UpdateNameFieldAction.UPDATE_PREFIX, value: '' })}
            disabled={state.locked}
          />
        )}
        <TextField
          label="First name"
          ref={inputRef}
          value={state.firstName}
          required
          onChange={(event) =>
            updateState({
              type: UpdateNameFieldAction.UPDATE_FIRST_NAME,
              value: event.currentTarget.value,
            })
          }
          onClear={() => updateState({ type: UpdateNameFieldAction.UPDATE_FIRST_NAME, value: '' })}
          disabled={state.locked}
        />
        {(fieldVisibility.preferredName || state.preferredName) && (
          <TextField
            value={state.preferredName}
            label="Preferred name"
            onChange={(event) =>
              updateState({
                type: UpdateNameFieldAction.UPDATE_PREFERRED_NAME,
                value: event.currentTarget.value,
              })
            }
            onClear={() => updateState({ type: UpdateNameFieldAction.UPDATE_PREFERRED_NAME, value: '' })}
            disabled={state.locked}
          />
        )}
        {(fieldVisibility.middleName || state.middleName) && (
          <TextField
            label="Middle name"
            value={state.middleName}
            onChange={(event) =>
              updateState({
                type: UpdateNameFieldAction.UPDATE_MIDDLE_NAME,
                value: event.currentTarget.value,
              })
            }
            onClear={() => updateState({ type: UpdateNameFieldAction.UPDATE_MIDDLE_NAME, value: '' })}
            disabled={state.locked}
          />
        )}
        <TextField
          label="Last name"
          value={state.lastName}
          onChange={(event) =>
            updateState({
              type: UpdateNameFieldAction.UPDATE_LAST_NAME,
              value: event.currentTarget.value,
            })
          }
          onClear={() => updateState({ type: UpdateNameFieldAction.UPDATE_LAST_NAME, value: '' })}
          disabled={state.locked}
        />

        {(fieldVisibility.honorificSuffix || state.honorificSuffix) && (
          <TextField
            value={state.honorificSuffix}
            label="Suffix"
            onChange={(event) =>
              updateState({
                type: UpdateNameFieldAction.UPDATE_SUFFIX,
                value: event.currentTarget.value,
              })
            }
            onClear={() => updateState({ type: UpdateNameFieldAction.UPDATE_SUFFIX, value: '' })}
            disabled={state.locked}
          />
        )}
        {(fieldVisibility.maidenName || state.maidenName) && (
          <TextField
            value={state.maidenName}
            label="Maiden name"
            onChange={(event) =>
              updateState({
                type: UpdateNameFieldAction.UPDATE_MAIDEN_NAME,
                value: event.currentTarget.value,
              })
            }
            onClear={() => updateState({ type: UpdateNameFieldAction.UPDATE_MAIDEN_NAME, value: '' })}
            disabled={state.locked}
          />
        )}
        {(fieldVisibility.pronouns || state.pronouns) && (
          <TextField
            value={state.pronouns}
            label="Pronouns"
            onChange={(event) =>
              updateState({
                type: UpdateNameFieldAction.UPDATE_PRONOUNS,
                value: event.currentTarget.value,
              })
            }
            onClear={() => updateState({ type: UpdateNameFieldAction.UPDATE_PRONOUNS, value: '' })}
            disabled={state.locked}
          />
        )}
        <Box style={{ maxWidth: '350px' }}>
          <NameFieldSuggestions
            fieldVisibility={fieldVisibility}
            setFieldVisibility={setFieldVisibility}
            disabled={state.locked}
          />
        </Box>
      </Stack>
    </div>
  );
};
